import type { ViewQueryRecordNodeWithId } from '@salescore/core'
import type { ReactElement } from 'react'

import type { RSheetRecordNode, UnionOfRSheetColumn } from '../../../domain/column'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes'
import { useGetLatestFieldChanges } from '../../../recoil/recordsHistory'
import type { SetMainCursorByRecordIdArguments } from '../../hooks/useSetMainCursorByRecordId'
import { type RenderCellGroup, RSheetRowMemo } from './RSheetRow'

/**
 * RSheet のボディ部分。**Body → Row → CellGroup → Cell** という入れ子構造を持ち、内側に
 * 行くほどネストが深くなります。
 *
 * 重要な実装ポイントとして、**行は仮想化されています (仮想スクロール)**。シートは 1 万行
 * 以上をレンダリングすることもありますが、それを全て描画すると非常に重くなるため、現在
 * 見えている行 (`virtualizedRows`) だけをレンダリングします。
 */
export const RSheetBody = <T extends UnionOfRSheetColumn<RSheetRecordNode>>({
  targetColumns,
  virtualizedRows,
  renderingRowsTopIndex,
  columnIndexStart,
  setDragging,
  expandOnDrag,
  visibleRowNumber,
  rowExpandable,
  setMainCursorByRecordId,
  renderCellGroup,
  rowHeight,
}: {
  targetColumns: T[]
  virtualizedRows: ViewQueryRecordNodeWithId[]
  renderingRowsTopIndex: number
  columnIndexStart: number
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  visibleRowNumber: number | undefined
  rowExpandable: (row: ViewQueryRecordNodeWithId) => boolean
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  renderCellGroup: RenderCellGroup<T>
  rowHeight: number
}): ReactElement => {
  const getLatestFieldChanges = useGetLatestFieldChanges()

  return (
    <>
      {virtualizedRows.map((row, virtualizedRowIndex) => (
        <RSheetRowMemo
          // keyをrow.idにすると、子レコードでソートをして親レコードが複数行になる際におかしくなるため注意
          key={row.id}
          row={row}
          targetColumns={targetColumns}
          rowIndex={virtualizedRowIndex + renderingRowsTopIndex}
          columnIndexStart={columnIndexStart}
          setMainCursorByRecordId={setMainCursorByRecordId}
          rowHeight={rowHeight}
          getLatestFieldChanges={getLatestFieldChanges}
          setDragging={setDragging}
          expandOnDrag={expandOnDrag}
          visibleRowNumber={visibleRowNumber}
          renderCellGroup={renderCellGroup}
          rowExpandable={rowExpandable}
        />
      ))}
    </>
  )
}
